/**
 * Backend API Integration Endpoints
 * Base URL (e.g. http://api.ledryt.com) is supplied via VITE_API_BASE_URL
 */
export const ENDPOINTS = {
  VISITORS: {
    REGISTER_WALK_IN: '/api/visitors/walk-in',
    WALK_IN: '/api/visitors/walk-in',
    REGISTER_EXPECTED: '/api/visitors/expected',
    METRICS: '/api/visitors/metrics',
    LOGS_STATS: '/api/visitors/logs/stats',
    EXPORT: '/api/visitors/export',
    HOSTS: '/api/visitors/hosts',
    LIST: '/api/visitors',
    GET_BY_ID: (id: string) => `/api/visitors/${id}`,
    BY_ID: (id: string) => `/api/visitors/${id}`,
    CHECK_IN: (id: string) => `/api/visitors/${id}/check-in`,
    CHECK_OUT: (id: string) => `/api/visitors/${id}/check-out`,
    CANCEL: (id: string) => `/api/visitors/${id}/cancel`,
    APPROVALS: '/api/visitors/approvals',
    APPROVE: (id: string) => `/api/visitors/approvals/${id}/approve`,
    REJECT: (id: string) => `/api/visitors/approvals/${id}/reject`,
  },
  SETTINGS: '/api/settings',
  SETTINGS_ID_FORMATS_PREVIEW: '/api/settings/id-formats/preview',
  CLASS_REPORTS: {
    OVERVIEW: '/api/class-reports/overview',
    STRENGTH_CAPACITY: '/api/class-reports/strength-capacity',
    STRENGTH_CAPACITY_EXPORT: '/api/class-reports/strength-capacity/export',
    STUDENT_DIRECTORY: '/api/class-reports/student-directory',
    STUDENT_DIRECTORY_EXPORT: '/api/class-reports/student-directory/export',
    ATTENDANCE_SUMMARY: '/api/class-reports/attendance-summary',
    ATTENDANCE_SUMMARY_EXPORT: '/api/class-reports/attendance-summary/export',
    ACADEMIC_PERFORMANCE: '/api/class-reports/academic-performance',
    ACADEMIC_PERFORMANCE_EXPORT: '/api/class-reports/academic-performance/export',
    STUDENT_RANKINGS: '/api/class-reports/student-rankings',
    STUDENT_RANKINGS_EXPORT: '/api/class-reports/student-rankings/export',
  },
  ORGANIZATION: {
    ME: '/api/organization/me',
    UPLOAD_LOGO: '/api/organization/me/logo',
    LOGO: '/api/organization/me/logo',
    DETAILS: '/api/organization/details',
    UPDATE: '/api/organization/details',
    KPIS: '/api/organization/kpis',
    SAAS_LOOKUP_VALUES: (typeCode: string) => `/api/saas-lookup-values?typeCode=${typeCode}`,
    LOOKUP_VALUES: (typeCode: string) => `/api/lookup-values?typeCode=${typeCode}`,
    SUBSCRIPTION_INVOICE_RECEIPT: (invoiceId: string) => {
      if (!invoiceId || typeof invoiceId !== 'string' || !invoiceId.trim()) {
        throw new Error('ENDPOINTS.ORGANIZATION.SUBSCRIPTION_INVOICE_RECEIPT: invoiceId parameter is empty, whitespace, or undefined')
      }
      return `/api/organization/subscription-invoices/${invoiceId.trim()}/receipt.pdf`
    },
  },
  BRANCHES: {
    LIST: '/api/branches',
    KPIS: '/api/branches/kpis',
    GET_BY_ID: (id: string) => {
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('ENDPOINTS.BRANCHES.GET_BY_ID: id parameter is empty, whitespace, or undefined')
      }
      return `/api/branches/${id.trim()}`
    },
    GET_SUBSCRIPTION: (id: string) => {
      if (!id || typeof id !== 'string' || !id.trim() || id.trim() === '""') {
        throw new Error('ENDPOINTS.BRANCHES.GET_SUBSCRIPTION: id parameter is empty, whitespace, or undefined')
      }
      return `/api/branches/${id.trim()}/subscription`
    },
    GET_SUBSCRIPTION_INVOICES: (id: string) => {
      if (!id || typeof id !== 'string' || !id.trim() || id.trim() === '""') {
        throw new Error('ENDPOINTS.BRANCHES.GET_SUBSCRIPTION_INVOICES: id parameter is empty, whitespace, or undefined')
      }
      return `/api/branches/${id.trim()}/subscription/invoices`
    },
    DEACTIVATE: (id: string) => {
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('ENDPOINTS.BRANCHES.DEACTIVATE: id parameter is empty, whitespace, or undefined')
      }
      return `/api/branches/${id.trim()}/deactivate`
    },
    ACTIVATE: (id: string) => {
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('ENDPOINTS.BRANCHES.ACTIVATE: id parameter is empty, whitespace, or undefined')
      }
      return `/api/branches/${id.trim()}/activate`
    },
    REQUEST_UPGRADE: (id: string) => {
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('ENDPOINTS.BRANCHES.REQUEST_UPGRADE: id parameter is empty, whitespace, or undefined')
      }
      return `/api/branches/${id.trim()}/request-upgrade`
    },
    REQUEST_NEW: '/api/branches/requests',
    WITHDRAW_REQUEST: (id: string) => {
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('ENDPOINTS.BRANCHES.WITHDRAW_REQUEST: id parameter is empty, whitespace, or undefined')
      }
      return `/api/branches/requests/${id.trim()}/withdraw`
    },
    ASSIGN_ADMIN: (id: string) => {
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('ENDPOINTS.BRANCHES.ASSIGN_ADMIN: id parameter is empty, whitespace, or undefined')
      }
      return `/api/branches/${id.trim()}/assign-admin`
    },
    CHANGE_ADMIN: (id: string) => {
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('ENDPOINTS.BRANCHES.CHANGE_ADMIN: id parameter is empty, whitespace, or undefined')
      }
      return `/api/branches/${id.trim()}/change-admin`
    },
  },
  AUDIT_LOGS: {
    LIST: '/api/audit-logs',
    EXPORT: '/api/audit-logs/export',
    STATS: '/api/audit-logs/stats',
    FILTERS: '/api/audit-logs/filters',
    DETAIL: (id: string) => `/api/audit-logs/${id}`,
    VERIFY: (id: string) => `/api/audit-logs/${id}/verify`,
  },
  ORG_TEMPLATES: {
    CATALOG: '/api/org-templates/catalog',
    RESOLVED: (templateKey: string) => `/api/org-templates/resolved/${templateKey}`,
    PREVIEW_PDF: (templateKey: string) => `/api/org-templates/preview-pdf/${templateKey}`,
    BASE: '/api/org-templates',
    CREATE: '/api/org-templates',
    BY_ID: (id: string) => `/api/org-templates/${id}`,
    UPDATE: (id: string) => `/api/org-templates/${id}`,
  },
  AUTH: {
    LOGIN: '/api/auth/login',
    RESET_PASSWORD: '/api/auth/reset-password',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    FORGOT_PASSWORD_VERIFY: '/api/auth/forgot-password/verify',
    FORGOT_PASSWORD_CONFIRM: '/api/auth/forgot-password/confirm',
    SUPERADMIN_LOGIN: '/api/auth/superadmin/login',
    REFRESH_TOKEN: '/api/auth/refresh',
    LOGOUT: '/api/auth/logout',
    CHANGE_PASSWORD: '/api/auth/change-password',
    ME: '/api/auth/me',
    ACT_AS_BRANCH: '/api/auth/act-as-branch',
  },
  ROLES: {
    LIST: '/api/roles',
    CREATE: '/api/roles',
    GET_BY_ID: (id: string) => `/api/roles/${id}`,
    UPDATE: (id: string) => `/api/roles/${id}`,
    DELETE: (id: string) => `/api/roles/${id}`,
    KPIS: '/api/roles/kpis',
    PERMISSION_MATRIX: '/api/roles/permission-matrix',
    ASSIGN_BRANCH_ROLE: (userId: string) => `/api/users/${userId}/branch-role`,
  },
  DEPARTMENTS: {
    LIST: '/api/departments',
    CREATE: '/api/departments',
    GET_BY_ID: (id: string) => `/api/departments/${id}`,
    UPDATE: (id: string) => `/api/departments/${id}`,
    DELETE: (id: string) => `/api/departments/${id}`,
  },
  DESIGNATIONS: {
    LIST: '/api/designations',
    CREATE: '/api/designations',
    GET_BY_ID: (id: string) => `/api/designations/${id}`,
    UPDATE: (id: string) => `/api/designations/${id}`,
    DELETE: (id: string) => `/api/designations/${id}`,
  },
  STAFF: {
    LIST: '/api/staff',
    TEACHERS: '/api/staff/teachers',
    GET_BY_ID: (id: string) => `/api/staff/${id}`,
    CLASSES: '/api/staff/classes',
    CREATE: '/api/staff',
    UPDATE: (id: string) => `/api/staff/${id}`,
    DELETE: (id: string) => `/api/staff/${id}`,
  },
  DASHBOARD: {
    ADMIN: '/api/dashboard/admin',
    STUDENT: '/api/dashboard/student',
    STAFF: '/api/dashboard/staff',
    KPIS: '/api/dashboard/kpis',
    KPIS_STUDENT: '/api/dashboard/kpis/student',
    KPIS_STAFF: '/api/dashboard/kpis/staff',
    KPIS_SEAT: '/api/dashboard/kpis/seat',
    KPIS_ACADEMIC: '/api/dashboard/kpis/academic',
    KPIS_SUBJECT: '/api/dashboard/kpis/subject',
    KPIS_USER: '/api/dashboard/kpis/user',
    KPIS_ROLE: '/api/dashboard/kpis/role',
    KPIS_DEPARTMENT: '/api/dashboard/kpis/department',
    KPIS_DESIGNATION: '/api/dashboard/kpis/designation',
    KPIS_TEACHER: '/api/dashboard/kpis/teacher',
    ORG_SUMMARY: '/api/dashboard/org/summary',
    ORG_BRANCH_PERFORMANCE: '/api/dashboard/org/branch-performance',
    ORG_ATTENTION_REQUIRED: '/api/dashboard/org/attention-required',
    ORG_REVENUE_TREND: '/api/dashboard/org/revenue-trend',
    ORG_FEE_PERFORMANCE: '/api/dashboard/org/fee-performance',
    ORG_ADMISSION_FUNNEL: '/api/dashboard/org/admission-funnel',
    ORG_GOVERNANCE_MATRIX: '/api/dashboard/org/governance-matrix',
    ORG_STUDENT_DISTRIBUTION: '/api/dashboard/org/student-distribution',
    ORG_ATTENDANCE_OVERVIEW: '/api/dashboard/org/attendance-overview',
  },
  ADMIN: {
    DASHBOARD: '/api/dashboard/admin',
    DASHBOARD_STUDENT: '/api/dashboard/student',
    DASHBOARD_STAFF: '/api/dashboard/staff',
    DASHBOARD_KPI: '/api/admin/dashboard/kpi',
    DASHBOARD_STUDENT_SNAPSHOT: '/api/admin/dashboard/student-snapshot',
    DASHBOARD_DEPARTMENT_OVERVIEW: '/api/admin/dashboard/department-overview',
    DASHBOARD_TEACHER_SNAPSHOT: '/api/admin/dashboard/teacher-snapshot',
    DASHBOARD_ACADEMIC_PERFORMANCE: '/api/admin/dashboard/academic-performance',
    DASHBOARD_PENDING_APPROVAL: '/api/admin/dashboard/pending-approvals',
    DASHBOARD_RECENT_COMMUNITY: '/api/admin/dashboard/recent-community',
    DASHBOARD_FEES_STATUS: '/api/admin/dashboard/fees-status',
    DASHBOARD_ATTENDANCE_ANALYTICS: '/api/admin/dashboard/attendance-analytics',
    DASHBOARD_RECENT_ACTIVITY: '/api/admin/dashboard/recent-activity',
    DASHBOARD_SCHEDULE_AND_LEAVE: '/api/admin/dashboard/schedule-and-leave',
  },
  ADMISSIONS: {
    DASHBOARD_KPI: '/api/admissions/dashboard/kpi',
    APPLICATION_LIST: '/api/admissions/applications',
  },
  ADMISSION_ENQUIRIES: {
    CREATE: '/api/admission-enquiries',
    LIST: '/api/admission-enquiries',
    GET_BY_ID: (id: string) => `/api/admission-enquiries/${id}`,
    UPDATE: (id: string) => `/api/admission-enquiries/${id}`,
    DELETE: (id: string) => `/api/admission-enquiries/${id}`,
    FOLLOWUPS: (id: string) => `/api/admission-enquiries/${id}/followups`,
    REJECT: (id: string) => `/api/admission-enquiries/${id}/reject`,
    CONVERT: (id: string) => `/api/admission-enquiries/${id}/convert-to-application`,
    STATS: '/api/admission-enquiries/stats',
    SIBLINGS_SEARCH: '/api/admission-enquiries/siblings/search',
  },
  ADMISSION_APPLICATIONS: {
    LIST: '/api/admission-applications',
    CREATE: '/api/admission-applications',
    GET_BY_ID: (id: string) => `/api/admission-applications/${id}`,
    DELETE: (id: string) => `/api/admission-applications/${id}`,
    STEP_1: (id: string) => `/api/admission-applications/${id}/step-1`,
    STEP_2: (id: string) => `/api/admission-applications/${id}/step-2`,
    STEP_3: (id: string) => `/api/admission-applications/${id}/step-3`,
    DOCUMENTS: (id: string) => `/api/admission-applications/${id}/documents`,
    DELETE_DOCUMENT: (id: string, documentId: string) => `/api/admission-applications/${id}/documents/${documentId}`,
    STEP_4: (id: string) => `/api/admission-applications/${id}/step-4`,
    STEP_5: (id: string) => `/api/admission-applications/${id}/step-5`,
    STEP_6: (id: string) => `/api/admission-applications/${id}/step-6`,
    SUBMIT: (id: string) => `/api/admission-applications/${id}/submit`,
  },
  ADMISSION_PROCESSING: {
    LIST: '/api/admission-processing',
    GET_BY_ID: (id: string) => `/api/admission-processing/${id}`,
    PREVIEW: (id: string) => `/api/admission-processing/${id}/preview`,
    DOCUMENTS: (id: string) => `/api/admission-processing/${id}/documents`,
    VERIFY_DOCUMENT: (id: string, documentId: string) => `/api/admission-processing/${id}/documents/${documentId}/verify`,
    REJECT_DOCUMENT: (id: string, documentId: string) => `/api/admission-processing/${id}/documents/${documentId}/reject`,
    REUPLOAD_REQUESTS: (id: string) => `/api/admission-processing/${id}/reupload-requests`,
    STAGE_1_RECEIVE: (id: string) => `/api/admission-processing/${id}/stage-1/receive`,
    STAGE_2_DOC_VERIFICATION: (id: string) => `/api/admission-processing/${id}/stage-2/document-verification`,
    STAGE_3_ASSESSMENT: (id: string) => `/api/admission-processing/${id}/stage-3/assessment`,
    STAGE_3_SEND_EMAIL: (id: string) => `/api/admission-processing/${id}/stage-3/send-email`,
    STAGE_4_SEAT_ALLOCATION: (id: string) => `/api/admission-processing/${id}/stage-4/seat-allocation`,
    CONFIRM_ADMISSION: (id: string) => `/api/admission-processing/${id}/confirm-admission`,
  },
  ASSIGNMENTS: {
    CREATE: '/api/assignments',
    LIST: '/api/assignments',
    SUBMISSION_TYPES: '/api/assignments/submission-types',
    GET_BY_ID: (id: string) => `/api/assignments/${id}`,
    CLOSE: (id: string) => `/api/assignments/${id}/close`,
    PUBLISH: (id: string) => `/api/assignments/${id}/publish`,
    DELETE: (id: string) => `/api/assignments/${id}`,
    SUBMISSIONS: (id: string) => `/api/assignments/${id}/submissions`,
    GRADE_SUBMISSION: (id: string, studentId: string) => `/api/assignments/${id}/submissions/${studentId}/grade`,
    DELETE_ATTACHMENT: (id: string, attachmentId: string) => `/api/assignments/${id}/attachments/${attachmentId}`,
    NUDGE: (id: string) => `/api/assignments/${id}/nudge`,
  },
  HOMEWORK: {
    KPIS: '/api/homework/kpis',
    SUBMISSION_MODES: '/api/homework/submission-modes',
    CREATE: '/api/homework',
    LIST: '/api/homework',
    GET_BY_ID: (id: string) => `/api/homework/${id}`,
    UPDATE: (id: string) => `/api/homework/${id}`,
    DELETE: (id: string) => `/api/homework/${id}`,
    PUBLISH: (id: string) => `/api/homework/${id}/publish`,
    CLOSE: (id: string) => `/api/homework/${id}/close`,
    DELETE_ATTACHMENT: (id: string, attachmentId: string) => `/api/homework/${id}/attachments/${attachmentId}`,
    SUBMISSIONS: (id: string) => `/api/homework/${id}/submissions`,
    REVIEW_SUBMISSION: (id: string, studentId: string) => `/api/homework/${id}/submissions/${studentId}/review`,
    FLAG_SUBMISSION: (id: string, studentId: string) => `/api/homework/${id}/submissions/${studentId}/flag`,
  },
  STUDENT: {
    ASSIGNMENTS: '/api/student/assignments',
    SUBMIT_ASSIGNMENT: '/api/student/assignments/submit',
  },
  SEAT_MANAGEMENT: {
    OVERVIEW: '/api/seat-management/overview',
    ROSTER: '/api/seat-management/roster',
    SECTION_SEAT_DETAIL: (id: string) => `/api/seat-management/sections/${id}`,
    SECTION_STUDENTS: (id: string) => `/api/seat-management/sections/${id}/students`,
    UPDATE_SECTION_CAPACITY: (id: string) => `/api/seat-management/sections/${id}/capacity`,
    UPDATE_SECTION_ADMISSION_STATUS: (id: string) => `/api/seat-management/sections/${id}/admission-status`,
    GRADES: '/api/grades/grades',
    GRADE_BY_ID: (id: string) => `/api/grades/grades/${id}`,
    GRADE_CRITERIA: '/api/grade-criteria',
    GRADE_CRITERIA_BY_ID: (id: string) => `/api/grade-criteria/${id}`,
    SECTIONS_BY_GRADE: (gradeId: string) => `/api/grades/${gradeId}/sections`,
    BULK_SECTIONS: (gradeId: string) => `/api/grades/${gradeId}/sections/bulk`,
    SECTIONS: '/api/sections',
    SECTION_BY_ID: (id: string) => `/api/sections/${id}`,
    LOOKUP_VALUES: (typeCode: string) => `/api/lookup-values?typeCode=${typeCode}`,
  },
  ACADEMICS: {
    GET_CURRENT_SESSION: '/api/academic-sessions/current',
    CREATE_SESSION: '/api/academic-sessions',
    GET_YEARS: '/api/academic-sessions',
    GET_YEAR_DETAILS: (id: string) => `/api/academic-sessions/${id}`,
    GET_STRUCTURE: (id: string) => `/api/academic-sessions/${id}/structure`,
    DOWNLOAD_REPORT: (id: string) => `/api/academic-sessions/${id}/report.pdf`,
    UPDATE_YEAR: (id: string) => `/api/academic-sessions/${id}`,
    DELETE_SESSION: (id: string) => `/api/academic-sessions/${id}`,
    CREATE_TERM: '/api/terms',
    GET_TERM_TYPES: '/api/lookup-values?typeCode=TERM_TYPE',
    GET_ACADEMIC_PERIOD_SYSTEM_TYPES: '/api/lookup-values?typeCode=ACADEMIC_PERIOD_SYSTEM',
    GET_ACADEMIC_SESSION_STATUS_TYPES: '/api/lookup-values?typeCode=ACADEMIC_SESSION_STATUS',
    CREATE_HOLIDAY: '/api/holidays',
    UPDATE_HOLIDAY: (id: string) => `/api/holidays/${id}`,
    DELETE_HOLIDAY: (id: string) => `/api/holidays/${id}`,
    GET_HOLIDAY_TYPES: '/api/lookup-values?typeCode=HOLIDAY_TYPE',
    GET_SEASONAL_BREAK_TYPES: '/api/lookup-values?typeCode=BREAK_TYPE',
    GET_IMPACT_GROUPS: '/api/lookup-values?typeCode=IMPACT_GROUP',
    CREATE_SEASONAL_BREAK: '/api/seasonal-breaks',
    UPDATE_SEASONAL_BREAK: (id: string) => `/api/seasonal-breaks/${id}`,
    DELETE_SEASONAL_BREAK: (id: string) => `/api/seasonal-breaks/${id}`,
  },
  SUBJECTS: {
    CREATE: '/api/subjects',
    LIST: '/api/subjects',
    SUBJECT_TYPES: '/api/subjects/subject-types',
    CATEGORIES: '/api/subjects/categories',
    BY_ID: (id: string) => `/api/subjects/${id}`,
    ALLOCATIONS: (id: string) => `/api/subjects/${id}/allocations`,
    ALLOCATE: '/api/subjects/allocate',
  },
  STUDENTS: {
    LIST: '/api/students',
    GET_BY_ID: (id: string) => `/api/students/${id}`,
    STAFF_VIEW: (id: string) => `/api/students/${id}/staff-view`,
    CREATE: '/api/students',
    UPDATE: (id: string) => `/api/students/${id}`,
    DELETE: (id: string) => `/api/students/${id}`,
    TRANSFER: (id: string) => `/api/students/${id}/transfers`,
    PROMOTIONS: {
      CANDIDATES: '/api/students/promotions/candidates',
      EXECUTE: '/api/students/promotions/execute',
    },
  },
  LOOKUP_VALUES: (typeCode: string) => `/api/lookup-values?typeCode=${typeCode}`,

  IMPORTS: {
    UPLOAD: '/api/imports',
    COMMIT: (importId: string) => `/api/imports/${importId}/commit`,
    TEMPLATE: '/api/imports/template',
    GET_BY_ID: (importId: string) => `/api/imports/${importId}`,
  },
  DOCUMENT_VERIFICATIONS: {
    QUEUE: '/api/document-verifications',
    HEADER: (studentId: string) => `/api/document-verifications/${studentId}`,
    VERIFY: (studentId: string) => `/api/document-verifications/${studentId}/verify`,
    REUPLOAD_REQUESTS: (studentId: string) => `/api/document-verifications/${studentId}/reupload-requests`,
    DOCUMENTS: (studentId: string) => `/api/document-verifications/${studentId}/documents`,
    UPLOAD_DOCUMENT: (studentId: string) => `/api/document-verifications/${studentId}/documents`,
    DOWNLOAD_DOCUMENT: (studentId: string, documentId: string) => `/api/document-verifications/${studentId}/documents/${documentId}/download`,
    DELETE_DOCUMENT: (studentId: string, documentId: string) => `/api/document-verifications/${studentId}/documents/${documentId}`,
    GET_DOCUMENT: (studentId: string, documentId: string) => `/api/document-verifications/${studentId}/documents/${documentId}`,
    LOG_DECISION: (studentId: string, documentId: string) => `/api/document-verifications/${studentId}/documents/${documentId}/logs`,
  },
  ACADEMIC_SESSIONS: {
    GET_CURRENT: '/api/academic-sessions/current',
    LIST: '/api/academic-sessions',
    OPTIONS: '/api/academic-sessions/options',
    SESSIONS: '/api/academic-sessions',
  },
  FEE_REPORTS: {
    OVERVIEW: '/api/fee-reports/overview',
    TRANSACTIONS: '/api/fee-reports/transactions',
    CLASS: '/api/fee-reports/class',
    REVENUE: '/api/fee-reports/revenue',
    PENDING_DUES: '/api/fee-reports/pending-dues',
    INVOICE_RECONCILIATION: '/api/fee-reports/invoice-reconciliation',
    STUDENT_WISE: (studentId: string) => `/api/fee-reports/student/${encodeURIComponent(studentId)}`,
    STUDENT_WISE_PDF: (studentId: string) => `/api/fee-reports/student/${encodeURIComponent(studentId)}/pdf`,
  },
  USERS: {
    LIST: '/api/users',
    KPIS: '/api/users/kpis',
    STUDENTS: '/api/users/students',
    LOOKUP: '/api/users/lookup',
    ENABLE_LOGIN: '/api/users',
    GET_BY_ID: (id: string) => `/api/users/${id}`,
  },
  COMMUNICATION: {
    DASHBOARD: '/api/communications/dashboard',
    MESSAGES: '/api/communications',
    CREATE: '/api/communications',
    DELETE: (id: string) => `/api/communications/${id}`,
    UPDATE: (id: string) => `/api/communications/${id}`,
    GET_BY_ID: (id: string) => `/api/communications/${id}`,
    TARGET_TYPES: '/api/communications/target-types',
    CHANNELS: '/api/communications/channels',
    SMS_TEMPLATES: '/api/communications/sms-templates',
    SEND: (id: string) => `/api/communications/${id}/send`,
    STATUS: (id: string) => `/api/communications/${id}/status`,
    TARGET_USER_COUNT: '/api/communications/target-user-count',
    CANCEL_SCHEDULE: (id: string) => `/api/communications/${id}/cancel-schedule`,
    ATTACHMENTS: '/api/communications/attachments',
    DELETE_ATTACHMENT: (id: string, attachmentId: string) => `/api/communications/${id}/attachments/${attachmentId}`,
    FAILED_RECIPIENTS: (id: string) => `/api/communications/${id}/failed-recipients`,
    RETRY_FAILED: (id: string) => `/api/communications/${id}/retry-failed`,
  },
  GRADES: {
    LIST: '/api/grades/grades',
    CREATE: '/api/grades/grades',
    GET_BY_ID: (id: string) => `/api/grades/grades/${id}`,
    UPDATE: (id: string) => `/api/grades/grades/${id}`,
    DELETE: (id: string) => `/api/grades/grades/${id}`,
    OCCUPANCY: '/api/grades/occupancy',
    DIRECTORY: '/api/grades/directory',
    OVERVIEW: (gradeId: string) => `/api/grades/${gradeId}/overview`,
    SECTIONS: {
      LIST: (gradeId: string) => `/api/grades/${gradeId}/sections`,
      ASSIGN_CLASS_TEACHER: (gradeId: string, sectionId: string) => `/api/grades/${gradeId}/sections/${sectionId}/class-teacher`,
    },
  },
  SUBJECT_ALLOCATIONS: {
    SUMMARY: '/api/subjects/allocations/summary',
    COHORTS: '/api/subjects/allocations/cohorts',
    SECTION_ROSTER: (sectionId: string) => `/api/subjects/allocations/sections/${encodeURIComponent(sectionId)}`,
  },
  DEVICE_TOKENS: {
    REGISTER: '/api/device-tokens',
  },
  ATTENDANCE: {
    CLASSES: '/api/attendance/classes',
    SECTION: (sectionId: string) => `/api/attendance/section/${sectionId}`,
    MARK: '/api/attendance/mark',
    MARK_GRADEWISE: '/api/attendance/mark-gradewise',
    GRADEWISE: '/api/attendance/gradewise',
    STUDENT_MONTHLY: (studentId: string) => `/api/attendance/student/${studentId}/monthly`,
    LOGS: '/api/attendance/logs',
  },
  NOTICES: {
    LIST: '/api/notices',
    CREATE: '/api/notices',
    UPDATE: (id: string) => `/api/notices/${id}/attachments`,
    DELETE: (id: string) => `/api/notices/${id}`,
    DETAILS: (id: string) => `/api/notices/${id}`,
    PUBLISH: (id: string) => `/api/notices/${id}/publish`,
    VIEWS: (id: string) => `/api/notices/${id}/views`,
    DELETE_ATTACHMENT: (id: string, attachmentId: string) => `/api/notices/${id}/attachments/${attachmentId}`,
  },
  LESSON_PLANS: {
    STATS: '/api/lesson-plans/stats',
    OPTIONS: '/api/lesson-plans/options',
    STAGE_ATTACHMENT: '/api/lesson-plans/attachments/staging',
    LIST: '/api/lesson-plans',
    CREATE: '/api/lesson-plans',
    COVERAGE: (id: string) => `/api/lesson-plans/${id}/coverage`,
    DETAIL: (id: string) => `/api/lesson-plans/${id}`,
    UPDATE: (id: string) => `/api/lesson-plans/${id}`,
    DELETE: (id: string) => `/api/lesson-plans/${id}`,
    PUBLISH: (id: string) => `/api/lesson-plans/${id}/publish`,
    DUPLICATE: (id: string) => `/api/lesson-plans/${id}/duplicate`,
    SPLIT_AND_UPDATE: (id: string) => `/api/lesson-plans/${id}/split-and-update`,
    ADD_CHAPTER: (id: string) => `/api/lesson-plans/${id}/chapters`,
    UPDATE_CHAPTER: (id: string, chapterId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}`,
    DELETE_CHAPTER: (id: string, chapterId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}`,
    ADD_TOPIC: (id: string, chapterId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}/topics`,
    UPDATE_TOPIC: (id: string, chapterId: string, topicId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}/topics/${topicId}`,
    DELETE_TOPIC: (id: string, chapterId: string, topicId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}/topics/${topicId}`,
    CHAPTER_COMPLETION: (id: string, chapterId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}/completion`,
  },
  NOTIFICATIONS: {
    UNREAD_COUNT: '/api/notifications/unread-count',
    LIST: '/api/notifications',
    MARK_ALL_READ: '/api/notifications/mark-all-read',
    MARK_READ: (id: string) => `/api/notifications/${id}/read`,
  },
  STAFF_PORTAL: {
    MESSAGES: '/api/staff-portal/messages',
    MESSAGE_BY_ID: (id: string) => `/api/staff-portal/messages/${id}`,
  },
  VEHICLES: {
    LIST: '/api/vehicles',
    LIGHTWEIGHT: '/api/vehicles/lightweight',
    BY_ID: (id: string) => `/api/vehicles/${id}`,
    CREATE: '/api/vehicles',
    UPDATE: (id: string) => `/api/vehicles/${id}`,
    DELETE: (id: string) => `/api/vehicles/${id}`,
    ACTIVATE: (id: string) => `/api/vehicles/${id}/activate`,
    DEACTIVATE: (id: string) => `/api/vehicles/${id}/deactivate`,
    ASSIGN_ROUTE: (id: string) => `/api/vehicles/${id}/assign-route`,
  },
  ROUTES: {
    LIST: '/api/routes',
    LIGHTWEIGHT: '/api/routes/lightweight',
    EXPORT_CSV: '/api/routes/export-csv',
    STOPS: (id: string) => `/api/routes/${id}/stops`,
    BY_ID: (id: string) => `/api/routes/${id}`,
    CREATE: '/api/routes',
    UPDATE: (id: string) => `/api/routes/${id}`,
    DELETE: (id: string) => `/api/routes/${id}`,
    IMPORT: '/api/routes/import',
  },
  TRANSPORT_STAFF: {
    LIST: '/api/transport-staff',
    LIGHTWEIGHT: '/api/transport-staff/lightweight',
    BY_ID: (id: string) => `/api/transport-staff/${id}`,
    CREATE: '/api/transport-staff',
    UPDATE: (id: string) => `/api/transport-staff/${id}`,
    DELETE: (id: string) => `/api/transport-staff/${id}`,
    DEACTIVATE: (id: string) => `/api/transport-staff/${id}/deactivate`,
    ACTIVATE: (id: string) => `/api/transport-staff/${id}/activate`,
    ASSIGNMENT_HISTORY: (id: string) => `/api/transport-staff/${id}/assignment-history`,
  },
  UPLOADS: {
    PRESIGN: '/api/uploads/presign',
  },
  TRANSPORT_ALLOCATION: {
    ALLOCATE: '/api/transport/allocations',
    LIST: '/api/transport/allocations',
    EXPORT_CSV: '/api/transport/allocations/export-csv',
    BY_STUDENT_ID: (studentId: string) => `/api/transport/allocations/${encodeURIComponent(studentId)}`,
    UPDATE: (studentId: string) => `/api/transport/allocations/${encodeURIComponent(studentId)}`,
    DELETE: (studentId: string) => `/api/transport/allocations/${encodeURIComponent(studentId)}`,
  },
  TRANSPORT_FEES: {
    BULK_BANDS: '/api/transport/fees/bands/bulk',
    BANDS_LIST: '/api/transport/fees/bands',
    STUDENT_FEES_LIST: '/api/transport/fees/students',
    EXPORT_EXCEL: '/api/transport/fees/students/export',
    EXPORT_CSV: '/api/transport/fees/students/export-csv',
    CREATE_OVERRIDE: '/api/transport/fees/overrides',
  },
  TRANSPORT_DOCUMENTS: {
    EXPIRING: '/api/transport/documents/expiring',
    EXPORT_CSV: '/api/transport/documents/export-csv',
  },
  EXAMS: '/api/exams',
  EXAMS_LIGHTWEIGHT: '/api/exams/lightweight',
  EXAM_TIMETABLES: {
    BASE: '/api/exam-timetables',
    LIST: '/api/exam-timetables',
    CREATE: '/api/exam-timetables',
    DETAIL: '/api/exam-timetables/detail',
    BY_ID: (id: string) => `/api/exam-timetables/${id}`,
  },
  MARKS_ENTRY: {
    OVERVIEW: '/api/marks-entry',
    SUBJECTS: '/api/marks-entry/subjects',
    STUDENTS: '/api/marks-entry/students',
    EXPORT_EXCEL: '/api/marks-entry/students/export-excel',
    APPROVE: '/api/marks-entry/subjects/approve',
    REJECT: '/api/marks-entry/subjects/reject',
    PUBLISH: '/api/marks-entry/sections/publish',
  },
  TEACHER_MARKS_ENTRY: {
    TASKS: '/api/teacher/examinations/marks-entry/tasks',
    STUDENTS: '/api/teacher/examinations/marks-entry/students',
    KPIS: '/api/teacher/examinations/marks-entry/kpis',
  },
  TEACHER_EXAMINATIONS: {
    TABULATION: '/api/teacher/examinations/tabulation',
    SUBJECT_RESULTS: '/api/teacher/examinations/subject-results',
    SUBJECT_RESULTS_EXPORT_CSV: '/api/teacher/examinations/subject-results/export-csv',
  },
  REPORT_CARDS: {
    GRADES: '/api/report-cards/grades',
    STUDENTS: '/api/report-cards/students',
    STUDENTS_EXPORT_CSV: '/api/report-cards/students/export-csv',
    STUDENT_PDF: (studentId: string) => `/api/report-cards/students/${studentId}/pdf`,
  },
  EXAM_REPORTS: {
    STUDENTS: '/api/exam-reports/students',
    STUDENTS_EXPORT_CSV: '/api/exam-reports/students/export-csv',
    SUBJECTS: '/api/exam-reports/subjects',
    SUBJECTS_EXPORT_CSV: '/api/exam-reports/subjects/export-csv',
  },
  GROUPS: {
    LIST: '/api/groups',
    CREATE: '/api/groups',
    DIRECTORY_STUDENTS: '/api/groups/directory/students',
    DIRECTORY_STAFF: '/api/groups/directory/staff',
    BY_ID: (id: string) => `/api/groups/${encodeURIComponent(id)}`,
    DELETE: (id: string) => `/api/groups/${encodeURIComponent(id)}`,
    SETTINGS: (id: string) => `/api/groups/${encodeURIComponent(id)}/settings`,
    DUPLICATE: (id: string) => `/api/groups/${encodeURIComponent(id)}/duplicate`,
    MEMBERS: (id: string) => `/api/groups/${encodeURIComponent(id)}/members`,
    ADD_MEMBERS: (id: string) => `/api/groups/${encodeURIComponent(id)}/members`,
    REMOVE_MEMBERS: (id: string) => `/api/groups/${encodeURIComponent(id)}/members`,
    UPDATE_MEMBER_ROLE: (id: string) => `/api/groups/${encodeURIComponent(id)}/members/role`,
    MESSAGES: (id: string) => `/api/groups/${encodeURIComponent(id)}/messages`,
    SEND_MESSAGE: (id: string) => `/api/groups/${encodeURIComponent(id)}/messages`,
    FILES: (id: string) => `/api/groups/${encodeURIComponent(id)}/files`,
  },
  HRMS: {
    ATTENDANCE_RECORDS: '/api/hrms/attendance/records',
    ATTENDANCE_RECORDS_MY: '/api/hrms/attendance/records/my',
    ATTENDANCE_MY_SUMMARY: '/api/hrms/attendance/records/my/summary',
    ATTENDANCE_RECORDS_BULK: '/api/hrms/attendance/records/bulk',
    ATTENDANCE_RECORD_BY_ID: (id: string) => `/api/hrms/attendance/records/${id}`,
    ATTENDANCE_RECORDS_EXPORT: '/api/hrms/attendance/records/export',
    ATTENDANCE_IMPORT_TEMPLATE: '/api/hrms/attendance/records/import/template',
    ATTENDANCE_IMPORT: '/api/hrms/attendance/records/import',
    ATTENDANCE_SETTINGS: '/api/hrms/attendance/settings',
    ATTENDANCE_BIOMETRIC_STATUS: '/api/hrms/attendance/settings/sources/biometric/status',
    ATTENDANCE_REGULARIZATIONS: '/api/hrms/attendance/regularizations',
    ATTENDANCE_REGULARIZATIONS_MY: '/api/hrms/attendance/regularizations/my',
    ATTENDANCE_REGULARIZATION_BY_ID: (id: string) =>
      `/api/hrms/attendance/regularizations/${id}`,
    ATTENDANCE_MY_OVERVIEW: '/api/hrms/attendance/records/my/overview',
    ATTENDANCE_REGULARIZATION_REVIEW: (id: string) =>
      `/api/hrms/attendance/regularizations/${id}/review`,
    LEAVE_POLICIES: '/api/hrms/leave/policies',
    LEAVE_POLICIES_MY: '/api/hrms/leave/policies/my',
    LEAVE_POLICY_MY_BY_ID: (id: string) => `/api/hrms/leave/policies/my/${id}`,
    LEAVE_POLICIES_DROPDOWN: '/api/hrms/leave/policies/dropdown',
    LEAVE_POLICY_ATTACHMENT_STAGE: '/api/hrms/leave/policies/attachment/stage',
    LEAVE_POLICY_BY_ID: (id: string) => `/api/hrms/leave/policies/${id}`,
    LEAVE_APPLICATIONS: '/api/hrms/leave/applications',
    LEAVE_APPLICATION_BY_ID: (id: string) =>
      `/api/hrms/leave/applications/${id}`,
    LEAVE_APPLICATION_REVIEW: (id: string) =>
      `/api/hrms/leave/applications/${id}/review`,
    LEAVE_APPLICATION_ATTACHMENT_STAGE: '/api/hrms/leave/applications/attachment/stage',
    LEAVE_BALANCES: '/api/hrms/leave/applications/balances',
    LEAVE_BALANCES_EXPORT: '/api/hrms/leave/applications/balances/export',
    LEAVE_APPLICATIONS_MY: '/api/hrms/leave/applications/my',
    LEAVE_BALANCES_MY: '/api/hrms/leave/applications/balances/my',
    LEAVE_OVERVIEW_MY: '/api/hrms/leave/applications/overview/my',
    LEAVE_APPLICATION_CANCEL: (id: string) =>
      `/api/hrms/leave/applications/${id}/cancel`,
    LEAVE_BALANCE_BY_STAFF: (staffId: string) => {
      if (!staffId || !staffId.trim()) {
        throw new Error('staffId is required')
      }
      return `/api/hrms/leave/applications/balances/${staffId.trim()}`
    },
    PAYROLL_SALARY_COMPONENTS: '/api/hrms/payroll/salary-components',
    PAYROLL_SALARY_COMPONENTS_EXPORT: '/api/hrms/payroll/salary-components/export',
    PAYROLL_SALARY_COMPONENTS_CHECK_CODE: '/api/hrms/payroll/salary-components/check-code',
    PAYROLL_SALARY_COMPONENT_BY_ID: (id: string) => `/api/hrms/payroll/salary-components/${id}`,
    PAYROLL_SALARY_COMPONENT_DEACTIVATE: (id: string) => `/api/hrms/payroll/salary-components/${id}/deactivate`,
    PAYROLL_SALARY_STRUCTURES: '/api/hrms/payroll/salary-structures',
    PAYROLL_SALARY_STRUCTURES_KPIS: '/api/hrms/payroll/salary-structures/kpis',
    PAYROLL_SALARY_STRUCTURES_EXPORT: '/api/hrms/payroll/salary-structures/export',
    PAYROLL_SALARY_STRUCTURE_BY_ID: (id: string) => `/api/hrms/payroll/salary-structures/${id}`,
    PAYROLL_SALARY_STRUCTURE_BY_DESIGNATION_TIER: '/api/hrms/payroll/salary-structures/by-designation-tier',
    STAFF_DOCUMENT_CRITERIA: '/api/staff-document-criteria',
    STAFF_DOCUMENT_CRITERIA_BY_ID: (id: string) => `/api/staff-document-criteria/${id}`,
    STAFF_DOCUMENT_CRITERIA_STATUS: (id: string) => `/api/staff-document-criteria/${id}/status`,
    STAFF_DOCUMENTS_METRICS: '/api/staff-documents/metrics',
    STAFF_DOCUMENTS: '/api/staff-documents',
    STAFF_DOCUMENTS_BY_STAFF_ID: (staffId: string) => `/api/staff-documents/${encodeURIComponent(staffId)}`,
    STAFF_DOCUMENTS_PREVIEW: (staffId: string, docId: string) =>
      `/api/staff-documents/${encodeURIComponent(staffId)}/documents/${encodeURIComponent(docId)}/preview`,
    STAFF_DOCUMENTS_DECISION: (staffId: string, docId: string) =>
      `/api/staff-documents/${encodeURIComponent(staffId)}/documents/${encodeURIComponent(docId)}/decision`,
    STAFF_DOCUMENTS_DELETE: (staffId: string, docId: string) =>
      `/api/staff-documents/${encodeURIComponent(staffId)}/documents/${encodeURIComponent(docId)}`,
    STAFF_DOCUMENTS_UPLOAD: (staffId: string) => `/api/staff-documents/${encodeURIComponent(staffId)}/upload`,
    STAFF_ONBOARDING_LIST: '/api/staff-onboarding',
    STAFF_ONBOARDING_STEP_1: '/api/staff-onboarding/step-1',
    STAFF_ONBOARDING_STEP_2: (sessionId: string) => `/api/staff-onboarding/${sessionId}/step-2`,
    STAFF_ONBOARDING_STEP_3: (sessionId: string) => `/api/staff-onboarding/${sessionId}/step-3`,
    STAFF_ONBOARDING_STEP_3_DOCUMENTS: (sessionId: string) => `/api/staff-onboarding/${sessionId}/step-3/documents`,
    STAFF_ONBOARDING_STEP_3_DOC_REVIEW: (sessionId: string, docId: string) => `/api/staff-onboarding/${sessionId}/step-3/documents/${docId}/review`,
    STAFF_ONBOARDING_STEP_3_DOC_DECISION: (sessionId: string, docId: string) => `/api/staff-onboarding/${sessionId}/step-3/documents/${docId}/decision`,
    STAFF_ONBOARDING_STEP_4: (sessionId: string) => `/api/staff-onboarding/${sessionId}/step-4`,
    STAFF_ONBOARDING_STEP_5: (sessionId: string) => `/api/staff-onboarding/${sessionId}/step-5`,
    STAFF_ONBOARDING_STEP_6: (sessionId: string) => `/api/staff-onboarding/${sessionId}/step-6`,
    STAFF_ONBOARDING_STEP_7: (sessionId: string) => `/api/staff-onboarding/${sessionId}/step-7`,
    STAFF_ONBOARDING_OFFER_LETTER_SIGNED: (sessionId: string) => `/api/staff-onboarding/${sessionId}/offer-letter/signed`,
    STAFF_ONBOARDING_BY_ID: (sessionId: string) => `/api/staff-onboarding/${sessionId}`,
    STAFF_ONBOARDING_PHOTO: (sessionId: string) => `/api/staff-onboarding/${sessionId}/photo`,
    // Employee Reports APIs
    PAYROLL_REPORTS_SUMMARY: '/api/hrms/payroll/reports/summary',
    PAYROLL_REPORTS_SUMMARY_EXPORT: '/api/hrms/payroll/reports/summary/export',
    PAYROLL_REPORTS_STAFF: (staffId: string) => `/api/hrms/payroll/reports/summary/staff/${encodeURIComponent(staffId)}`,
    PAYROLL_REPORTS_STAFF_STATEMENT: (staffId: string) => `/api/hrms/payroll/reports/summary/staff/${encodeURIComponent(staffId)}/statement`,
    ATTENDANCE_REPORTS_SUMMARY: '/api/hrms/attendance/reports/summary',
    ATTENDANCE_REPORTS_SUMMARY_EXPORT: '/api/hrms/attendance/reports/summary/export',
    ATTENDANCE_REPORTS_STAFF: (staffId: string) => `/api/hrms/attendance/reports/summary/staff/${encodeURIComponent(staffId)}`,
    LEAVE_REPORTS_SUMMARY: '/api/hrms/leave/reports/summary',
    LEAVE_REPORTS_SUMMARY_EXPORT: '/api/hrms/leave/reports/summary/export',
    LEAVE_REPORTS_STAFF: (staffId: string) => `/api/hrms/leave/reports/summary/staff/${encodeURIComponent(staffId)}`,
    LEAVE_REPORTS_STAFF_STATEMENT: (staffId: string) => `/api/hrms/leave/reports/summary/staff/${encodeURIComponent(staffId)}/statement`,
    STAFF_DOCUMENT_REPORTS_SUMMARY: '/api/staff-documents/reports/summary',
    STAFF_DOCUMENT_REPORTS_SUMMARY_EXPORT: '/api/staff-documents/reports/summary/export',
    STAFF_DOCUMENT_REPORTS_STAFF: (staffId: string) => `/api/staff-documents/reports/summary/staff/${encodeURIComponent(staffId)}`,
    STAFF_DOCUMENT_REPORTS_STAFF_STATEMENT: (staffId: string) => `/api/staff-documents/reports/summary/staff/${encodeURIComponent(staffId)}/statement`,
    // HRMS Dashboard APIs
    DASHBOARD: '/api/hrms/dashboard',
    DASHBOARD_SUMMARY: '/api/hrms/dashboard/summary',
    DASHBOARD_ATTENDANCE: '/api/hrms/dashboard/attendance',
    DASHBOARD_PENDING_LEAVES: '/api/hrms/dashboard/pending-leaves',
    DASHBOARD_STAFF_OVERVIEW: '/api/hrms/dashboard/staff-overview',
    DASHBOARD_PAYROLL: '/api/hrms/dashboard/payroll',
    // Payroll Processing & Cycles APIs
    PAYROLL_PROCESSING_CYCLES: '/api/hrms/payroll/processing/cycles',
    PAYROLL_PROCESSING_CYCLES_BY_PERIOD: '/api/hrms/payroll/processing/cycles/by-period',
    PAYROLL_PROCESSING_CYCLE_BY_ID: (id: string) => `/api/hrms/payroll/processing/cycles/${encodeURIComponent(id)}`,
    PAYROLL_PROCESSING_CALCULATE: '/api/hrms/payroll/processing/cycles/calculate',
    PAYROLL_PROCESSING_FINALIZE: (id: string) => `/api/hrms/payroll/processing/cycles/${encodeURIComponent(id)}/finalize`,
    PAYROLL_PROCESSING_DISMISS_FLAGS: (id: string) => `/api/hrms/payroll/processing/cycles/${encodeURIComponent(id)}/dismiss-flags`,
    PAYROLL_PROCESSING_CYCLE_EMPLOYEES: (id: string) => `/api/hrms/payroll/processing/cycles/${encodeURIComponent(id)}/employees`,
    PAYROLL_PROCESSING_ITEM_BY_ID: (id: string) => `/api/hrms/payroll/processing/items/${encodeURIComponent(id)}`,
    PAYROLL_PROCESSING_ITEM_PAYSLIP_PDF: (id: string) => `/api/hrms/payroll/processing/items/${encodeURIComponent(id)}/payslip-pdf`,
    PAYROLL_PROCESSING_RESEND_PAYSLIPS_BULK: (id: string) => `/api/hrms/payroll/processing/cycles/${encodeURIComponent(id)}/resend-payslips`,
    PAYROLL_PROCESSING_RESEND_PAYSLIP_ITEM: (id: string) => `/api/hrms/payroll/processing/items/${encodeURIComponent(id)}/resend-payslip`,
    // Staff Self-Service Payroll APIs
    MY_PAYSLIPS_OVERVIEW: '/api/hrms/payroll/my/payslips/overview',
    MY_PAYSLIPS: '/api/hrms/payroll/my/payslips',
    MY_PAYSLIP_PDF: (id: string) => `/api/hrms/payroll/my/payslips/${encodeURIComponent(id)}/pdf`,
    MY_SALARY_STRUCTURE: '/api/hrms/payroll/my/salary-structure',
    MY_SALARY_STRUCTURE_PDF: '/api/hrms/payroll/my/salary-structure/pdf',
    // Payroll Overview APIs
    PAYROLL_OVERVIEW: '/api/hrms/payroll/overview',
    PAYROLL_OVERVIEW_EXCEPTIONS: '/api/hrms/payroll/overview/exceptions',
    PAYROLL_OVERVIEW_AUDIT_OVERRIDES: '/api/hrms/payroll/overview/audit-overrides',
    // Attendance Overview APIs
    ATTENDANCE_OVERVIEW_SUMMARY: '/api/hrms/attendance/overview/summary',
    ATTENDANCE_OVERVIEW_TODAY: '/api/hrms/attendance/overview/today',
    ATTENDANCE_OVERVIEW_PENDING_REQUESTS: '/api/hrms/attendance/overview/pending-requests',
  },
  LOOKUP: {
    VALUES: '/api/lookup-values',
    VALUE_BY_ID: (id: string) => `/api/lookup-values/${id}`,
  },
  STUDENT_ENROLLMENT: {
    BASE: '/api/student-enrollment',
    STEP_1: '/api/student-enrollment/step-1',
    STEP_1_BY_ID: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/step-1`,
    STEP_2: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/step-2`,
    STEP_3: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/step-3`,
    GRADE_DOCUMENTS: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/grade-documents`,
    DOCUMENTS: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/documents`,
    DOCUMENT_BY_ID: (id: string, documentId: string) =>
      `/api/student-enrollment/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}`,
    STEP_4: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/step-4`,
    SIBLINGS: '/api/student-enrollment/siblings',
    STEP_5: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/step-5`,
    SECTIONS: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/sections`,
    STEP_6: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/step-6`,
    DOSSIER: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/dossier`,
    CONFIRM: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}/confirm`,
    BY_ID: (id: string) => `/api/student-enrollment/${encodeURIComponent(id)}`,
  },
} as const
